import { DatePicker, Form, Input, InputNumber, Modal, Select, message } from 'antd'
import { StakingErc3643RewardTokenRow } from 'Modules'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import { stakingErc3643Api } from 'api/staking-erc3643-api'
import {
  createStakingPoolWithChain,
  useCreateStakingPool3643,
} from 'hooks/staking-erc3643/use-create-staking-pool-3643'
import { logError } from 'utils/logger'

import { StakingPoolRow } from '../types'

type Props = {
  open: boolean
  mode: 'create' | 'edit'
  pool?: StakingPoolRow | null
  rewardTokens: StakingErc3643RewardTokenRow[]
  loadingRewardTokens?: boolean
  onClose: () => void
  onSuccess: () => void
}

const formItemLayout = {
  labelCol: { xs: { span: 24 }, sm: { span: 6 } },
  wrapperCol: { xs: { span: 24 }, sm: { span: 16 } },
}

/** SRS 5.2 Select PTO — partial match on name or symbol, case-insensitive. */
const filterPtoOption = (input: string, option?: { label?: string }) => {
  const needle = input.trim().toLowerCase()
  if (!needle) return true
  return (option?.label ?? '').toLowerCase().includes(needle)
}

const PoolFormModal = ({
  open,
  mode,
  pool,
  rewardTokens,
  loadingRewardTokens,
  onClose,
  onSuccess,
}: Props) => {
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [ptoOptions, setPtoOptions] = useState<Array<{ value: string; label: string }>>([])
  const [loadingPtos, setLoadingPtos] = useState(false)
  const { address } = useAccount()
  const { deployCreatePoolStaking, isAgent } = useCreateStakingPool3643()

  const rewardTokenAddress = Form.useWatch('rewardToken', form) as string | undefined

  // New pools are always fixed reward; legacy APR pools keep editing APR
  const isFixedReward = mode === 'create' || pool?.rewardModel === 'FIXED_REWARD'
  const rewardTokenDecimals = (
    mode === 'create'
      ? rewardTokens.find((t) => t.address === rewardTokenAddress)
      : rewardTokens.find((t) => t.symbol === pool?.rewardToken)
  )?.decimals
  const rateLabel = isFixedReward ? 'Reward Per PTO/Year' : 'APR'

  const loadPtos = useCallback(async (rewardToken?: string) => {
    if (mode !== 'create' || !rewardToken) {
      setPtoOptions([])
      return
    }
    setLoadingPtos(true)
    try {
      const res = await stakingErc3643Api.getSelectTokenize(rewardToken)
      if (res.data?.success) {
        setPtoOptions(
          (res.data.data ?? []).map((row) => ({
            value: row._id,
            label: `${row.fullName} — ${row.symbol}`,
          }))
        )
      }
    } catch (error) {
      logError('Load select PTO failed', error)
      setPtoOptions([])
    } finally {
      setLoadingPtos(false)
    }
  }, [mode])

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && pool) {
      form.setFieldsValue({
        poolLabel: `${pool.ptoName} — ${pool.ptoSymbol}`,
        rewardToken: pool.rewardToken,
        rewardPerYear: pool.rewardPerYear,
        apr: pool.apr,
        startDate: dayjs(pool.startDate),
        endDate: dayjs(pool.endDate),
      })
    } else {
      form.resetFields()
      setPtoOptions([])
    }
  }, [form, mode, open, pool])

  useEffect(() => {
    if (!open || mode !== 'create') return
    form.setFieldValue('pto', undefined)
    loadPtos(rewardTokenAddress)
  }, [open, mode, rewardTokenAddress, loadPtos, form])

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        setSubmitting(true)
        try {
          if (mode === 'create') {
            if (!address) {
              message.warning('Connect wallet to deploy the staking pool on-chain')
            } else if (isAgent === false) {
              message.warning('Connected wallet is not a PTO Orchestrator agent')
            }
            const startAt = (values.startDate as Dayjs).toISOString()
            const endAt = (values.endDate as Dayjs).toISOString()
            await createStakingPoolWithChain(
              {
                tokenizeId: values.pto as string,
                rewardToken: values.rewardToken as string,
                rewardPerYear: String(values.rewardPerYear),
                startAt,
                endAt,
              },
              address,
              deployCreatePoolStaking
            )
            message.success('Create successfully')
          } else if (pool) {
            const startAt =
              pool.status === 'Scheduled' && values.startDate
                ? (values.startDate as Dayjs).toISOString()
                : undefined
            if (pool.status === 'Active' && pool.totalStakers > 0) {
              await new Promise<void>((resolve, reject) => {
                Modal.confirm({
                  title: `Change ${rateLabel}?`,
                  content: `This pool already has stakers. Changing ${rateLabel} will affect reward calculation from now on. Do you want to continue?`,
                  onOk: () => resolve(),
                  onCancel: () => reject(new Error('cancelled')),
                })
              })
            }
            const res = await stakingErc3643Api.updatePool(pool.id, {
              ...(isFixedReward
                ? { rewardPerYear: String(values.rewardPerYear) }
                : { apr: Number(values.apr) }),
              startAt: startAt ?? null,
              endAt: (values.endDate as Dayjs).toISOString(),
            })
            if (!res.data?.success) {
              throw new Error(res.data?.message ?? 'Update failed')
            }
            message.success('Update successfully')
          }
          form.resetFields()
          onSuccess()
          onClose()
        } catch (error) {
          if ((error as Error)?.message !== 'cancelled') {
            logError('Staking pool form submit failed', error)
            message.error((error as Error)?.message ?? 'Request failed')
          }
        } finally {
          setSubmitting(false)
        }
      })
      .catch(() => undefined)
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  const disablePastStartDate = (current: Dayjs) =>
    Boolean(current && current.isBefore(dayjs().startOf('day')))

  return (
    <Modal
      title={mode === 'create' ? 'Create Staking Pool' : 'Edit Staking Pool'}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      className="modalStaking"
      destroyOnClose
      width={720}
      centered
      confirmLoading={submitting}
      okText={mode === 'create' ? 'Create' : 'Save Changes'}
      cancelText="Cancel"
    >
      <Form
        form={form}
        {...formItemLayout}
        colon={false}
        requiredMark={(label, { required }) => (
          <>
            {required ? <span className="staking-3643-required">*</span> : null}
            {label}
          </>
        )}
        style={{ maxWidth: 680 }}
      >
        {mode === 'create' ? (
          <>
            <Form.Item
              label="Reward Token"
              name="rewardToken"
              rules={[{ required: true, message: 'Reward token is required' }]}
            >
              <Select
                placeholder="Select reward token"
                loading={loadingRewardTokens}
                options={rewardTokens.map((t) => ({
                  value: t.address,
                  label: t.symbol,
                }))}
              />
            </Form.Item>
            <Form.Item
              label="Select PTO"
              name="pto"
              rules={[{ required: true, message: 'PTO is required' }]}
            >
              <Select
                showSearch
                placeholder="Choose PTO"
                loading={loadingPtos}
                disabled={!rewardTokenAddress}
                options={ptoOptions}
                filterOption={filterPtoOption}
                optionFilterProp="label"
                popupClassName="staking-3643-pto-select-dropdown"
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item label="Pool" name="poolLabel">
              <Input disabled />
            </Form.Item>
            <Form.Item label="Reward Token" name="rewardToken">
              <Input disabled />
            </Form.Item>
          </>
        )}

        {isFixedReward ? (
          <Form.Item
            label="Reward Per PTO/Year"
            name="rewardPerYear"
            dependencies={['rewardToken']}
            rules={[
              { required: true, message: 'Reward Per PTO/Year is required' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '')
                    return Promise.resolve()
                  if (!(Number(value) > 0)) {
                    return Promise.reject(new Error('Reward Per PTO/Year must be greater than 0'))
                  }
                  // BE rejects more decimals than the reward token supports (MEY 18, USDC 6)
                  const fraction = String(value).split('.')[1] ?? ''
                  if (rewardTokenDecimals !== undefined && fraction.length > rewardTokenDecimals) {
                    return Promise.reject(
                      new Error(`Reward Per PTO/Year allows up to ${rewardTokenDecimals} decimals`)
                    )
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            {/* stringMode keeps all 18 decimals of MEY; precision would pad the display */}
            <InputNumber
              stringMode
              min="0"
              style={{ width: '100%' }}
              placeholder="Enter reward per PTO per year"
              controls={false}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label="APR"
            name="apr"
            rules={[
              { required: true, message: 'APR is required' },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '')
                    return Promise.resolve()
                  return value > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('APR must be greater than 0'))
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              addonAfter="%"
              placeholder="0.00"
              controls={false}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Start Date"
          name="startDate"
          rules={[{ required: true, message: 'Start Date is required' }]}
        >
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm:ss"
            style={{ width: '100%' }}
            placeholder="dd/mm/yyyy hh:mm:ss"
            disabled={mode === 'edit' && pool?.status === 'Active'}
            disabledDate={mode === 'create' ? disablePastStartDate : undefined}
          />
        </Form.Item>

        <Form.Item
          label="End Date"
          name="endDate"
          rules={[
            { required: true, message: 'End Date is required' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const start = getFieldValue('startDate') as Dayjs | undefined
                if (!value || !start || value.isAfter(start)) return Promise.resolve()
                return Promise.reject(new Error('End Date must be later than Start Date'))
              },
            }),
          ]}
        >
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm:ss"
            style={{ width: '100%' }}
            placeholder="dd/mm/yyyy hh:mm:ss"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default PoolFormModal
